import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Divider,
  Group,
  Loader,
  Menu,
  NumberInput,
  Progress,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconArrowLeft,
  IconChevronDown,
  IconChevronLeft,
  IconCircle,
  IconCircleCheck,
  IconDeviceFloppy,
  IconLock,
  IconMail,
  IconPhone,
  IconSend,
  IconTrash,
  IconUser,
} from "@tabler/icons-react";
import PageContent from "../shared/PageContent/PageContent";
import DealReview from "../Organization/DealReview";
import AddBonusForm from "../Organization/AddBonusForm";
import AddExpenseForm from "../Organization/AddExpenseForm";
import AddUpfrontPayForm from "../Organization/AddUpfrontPayForm";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import { handleApiError, handleGenericError } from "../../utils/errorHandler";
import { formatPhoneNumber } from "../../utils/formatters";
import {
  BONUS_CATEGORY_LABELS,
  EXPENSE_CATEGORY_LABELS,
  ORANGE,
  UPFRONT_PAY_TYPE_LABELS,
  bonusRows,
  bonusThreshold,
  currency,
  expenseRows,
  percent,
  titleize,
  toScaleLines,
  upfrontConfig,
} from "./repDealDisplay";

// The four steps of the offer flow, mirroring the iOS Send Offer screen:
// contact + guarantor, template pick, editable pay, then the review document.
const STEPS = ["Offer Details", "Offer Template", "Pay Details", "Review"];

// Scale lines sorted by revenue with the base tier pinned to 0, which is how the
// deal endpoints expect a scale to arrive.
const normalizeScaleLines = lines =>
  toScaleLines(lines)
    .slice()
    .sort((a, b) => a.revenue_amount - b.revenue_amount)
    .map((line, i) => (i === 0 ? { ...line, revenue_amount: 0 } : line));

// Group company_default_scales by scale_type so each editable table can offer
// the company's saved scales as presets.
const groupScalesByType = scales => {
  const out = { COMMISSION: [], OVERRIDE: [], MD: [] };
  (Array.isArray(scales) ? scales : []).forEach(scale => {
    const type = scale.scale_type;
    if (!out[type]) out[type] = [];
    out[type].push({
      ...scale,
      scale_lines: Array.isArray(scale.default_scale_lines)
        ? scale.default_scale_lines
        : scale.scale_lines || [],
    });
  });
  return out;
};

// A template bonus → the deal-create bonus payload. Only the fields the DTO
// accepts are sent; the global whitelist validation rejects anything else.
const templateBonus = b => ({
  name: b.name,
  bonus_type_category: b.bonus_type_category,
  bonus_type_earned_by_item: b.bonus_type_earned_by_item,
  bonus_type_earned_by_type: b.bonus_type_earned_by_type,
  item_name: b.item_name ?? null,
  item_monetary_value: b.item_monetary_value ?? null,
  bonus_amount: b.bonus_amount ?? null,
  upfront_bonus_amount: b.upfront_bonus_amount ?? null,
  bonus_revenue_amount: b.bonus_revenue_amount ?? null,
  bonus_accounts_amount: b.bonus_accounts_amount ?? null,
  bonus_recruits_amount: b.bonus_recruits_amount ?? null,
  recurring_initial_amount: b.recurring_initial_amount ?? null,
  recurring_repeated_amount: b.recurring_repeated_amount ?? null,
  recurring_start_date: b.recurring_start_date ?? null,
  recurring_end_date: b.recurring_end_date ?? null,
  recurring_schedule_type: b.recurring_schedule_type ?? null,
  recurring_day_of_week: b.recurring_day_of_week ?? null,
  recurring_day_of_month: b.recurring_day_of_month ?? null,
});

const templateExpense = e => ({
  name: e.name,
  expense_type_category: e.expense_type_category,
  backend_percentage: e.backend_percentage ?? null,
  backend_fixed_amount: e.backend_fixed_amount ?? null,
  recurring_frontend_amount: e.recurring_frontend_amount ?? null,
  recurring_schedule_type: e.recurring_schedule_type ?? null,
  recurring_day_of_week: e.recurring_day_of_week ?? null,
  recurring_day_of_month: e.recurring_day_of_month ?? null,
});

// A deal user's upfront pay snapshot → the deal-create upfront payload. The DTO
// has no `name` / `document_text_id`, so they are dropped here rather than
// spread through.
const templateUpfrontPay = up => {
  if (!up) return undefined;
  const payload = {
    upfront_pay_type: up.upfront_pay_type ?? null,
    fixed_amount: up.fixed_amount ?? null,
    fixed_percentage: up.fixed_percentage ?? null,
  };
  if (Array.isArray(up.scale_lines) && up.scale_lines.length > 0) {
    payload.scale_lines = up.scale_lines.map(l => ({
      accounts_amount: l.accounts_amount ?? null,
      amount: l.amount ?? null,
      percentage: l.percentage ?? null,
    }));
  }
  return payload;
};

// The deal-user key an offer's upfront pay / bonuses are collected under. A
// template with no deal users still collects one seller's pay, under "single".
const dealUserKey = dealUser => String(dealUser?.id ?? "single");

// Inline-editable rows for an added upfront pay, mirroring the iOS
// `upfrontRows`: the amounts are edited in place, while the pay type itself
// changes by removing and re-adding.
const upfrontFields = (up, update) => {
  const out = [];
  if (up.fixed_amount != null) {
    out.push({
      label: "Amount",
      unit: "money",
      value: up.fixed_amount,
      onChange: v => update({ ...up, fixed_amount: v }),
    });
  }
  if (up.fixed_percentage != null) {
    out.push({
      label: "Percentage",
      unit: "percent",
      value: up.fixed_percentage,
      onChange: v => update({ ...up, fixed_percentage: v }),
    });
  }
  (up.scale_lines || []).forEach((line, i) => {
    const isAmount = line.amount != null;
    if (!isAmount && line.percentage == null) return;
    const accounts = Number(line.accounts_amount) || 0;
    out.push({
      label: `${accounts}+ accounts`,
      unit: isAmount ? "money" : "percent",
      value: isAmount ? line.amount : line.percentage,
      onChange: v =>
        update({
          ...up,
          scale_lines: up.scale_lines.map((l, index) =>
            index === i ? { ...l, [isAmount ? "amount" : "percentage"]: v } : l
          ),
        }),
    });
  });
  return out;
};

const isRecurringBonus = category =>
  category === "RECURRING" || category === "REOCCURRING";

// Inline-editable rows for an added bonus. The amounts and the earning
// threshold are edited in place; the category and metric change via the form.
const bonusFields = (b, update) => {
  const out = [];
  if (b.bonus_type_category === "ITEM") {
    const value = b.item_monetary_value
      ? ` (${currency(b.item_monetary_value)})`
      : "";
    out.push({ label: "Item", text: `${b.item_name || "Item"}${value}` });
  } else if (isRecurringBonus(b.bonus_type_category)) {
    if (b.recurring_initial_amount != null) {
      out.push({
        label: "Initial",
        unit: "money",
        value: b.recurring_initial_amount,
        onChange: v => update({ ...b, recurring_initial_amount: v }),
      });
    }
    if (b.recurring_repeated_amount != null) {
      const schedule = (b.recurring_schedule_type || "").toLowerCase();
      out.push({
        label: schedule ? `Recurring (${schedule})` : "Recurring",
        unit: "money",
        value: b.recurring_repeated_amount,
        onChange: v => update({ ...b, recurring_repeated_amount: v }),
      });
    }
  } else if (b.bonus_amount != null) {
    out.push({
      label: "Amount",
      unit: "money",
      value: b.bonus_amount,
      onChange: v => update({ ...b, bonus_amount: v }),
    });
  }

  const qualifier = [b.bonus_type_earned_by_type, b.bonus_type_earned_by_item]
    .filter(Boolean)
    .map(v => String(v).toLowerCase().replace(/_/g, " "))
    .join(" ");
  const label = qualifier ? `Earned at (${qualifier})` : "Earned at";
  switch (b.bonus_type_earned_by_item) {
    case "REVENUE":
      out.push({
        label,
        unit: "money",
        value: b.bonus_revenue_amount,
        onChange: v => update({ ...b, bonus_revenue_amount: v }),
      });
      break;
    case "ACCOUNTS":
      out.push({
        label,
        value: b.bonus_accounts_amount,
        onChange: v => update({ ...b, bonus_accounts_amount: v }),
      });
      break;
    case "RECRUITS":
      out.push({
        label,
        value: b.bonus_recruits_amount,
        onChange: v => update({ ...b, bonus_recruits_amount: v }),
      });
      break;
    default:
      out.push({ label: "Earned when", text: bonusThreshold(b) });
  }
  return out;
};

// Inline-editable rows for an added expense.
const expenseFields = (e, update) => {
  const out = [];
  if (e.backend_fixed_amount != null) {
    out.push({
      label: "Amount",
      unit: "money",
      value: e.backend_fixed_amount,
      onChange: v => update({ ...e, backend_fixed_amount: v }),
    });
  }
  if (e.backend_percentage != null) {
    out.push({
      label: "Percentage",
      unit: "percent",
      value: e.backend_percentage,
      onChange: v => update({ ...e, backend_percentage: v }),
    });
  }
  if (e.recurring_frontend_amount != null) {
    const schedule = (e.recurring_schedule_type || "").toLowerCase();
    out.push({
      label: schedule ? `Recurring (${schedule})` : "Recurring",
      unit: "money",
      value: e.recurring_frontend_amount,
      onChange: v => update({ ...e, recurring_frontend_amount: v }),
    });
  }
  if (out.length === 0) {
    out.push({
      label: "Type",
      text:
        EXPENSE_CATEGORY_LABELS[e.expense_type_category] ||
        titleize(e.expense_type_category),
    });
  }
  return out;
};

const sellerIsValid = seller =>
  (seller?.name || "").trim().length > 1 &&
  /^\S+@\S+\.\S+$/.test((seller?.email || "").trim());

// An editable revenue → percentage scale table. The first ("Start") row's
// revenue is locked at 0; the header doubles as a preset picker when the company
// has saved scales of this type. `readOnly` shows the same table with plain
// values, a lock on every row and no preset picker or add footer — a
// non-customizable template's scale can be read but not tuned.
function ScaleTable({
  title,
  subtitle,
  col2Label,
  lines,
  onChange,
  presets,
  readOnly = false,
}) {
  const rows =
    lines.length > 0 ? lines : [{ revenue_amount: 0, percentage: 0 }];

  const updateLine = (index, field, value) =>
    onChange(rows.map((l, i) => (i === index ? { ...l, [field]: value } : l)));

  const addLine = () =>
    onChange([...rows, { revenue_amount: 0, percentage: 0 }]);

  const removeLine = index => onChange(rows.filter((_, i) => i !== index));

  const hasPresets = !readOnly && Array.isArray(presets) && presets.length > 0;

  const lockIcon = <IconLock size={14} color="var(--mantine-color-gray-5)" />;

  return (
    <Card withBorder radius="md" p={0} style={{ overflow: "hidden" }}>
      {hasPresets ? (
        <Menu withinPortal position="bottom" width="target">
          <Menu.Target>
            <UnstyledButton style={{ width: "100%" }}>
              <Stack gap={0} py="xs">
                <Group justify="center" gap={4}>
                  <Text fw={700} size="sm">
                    {title}
                  </Text>
                  <IconChevronDown size={14} />
                </Group>
                {subtitle && (
                  <Text ta="center" size="xs" c="dimmed">
                    {subtitle}
                  </Text>
                )}
              </Stack>
            </UnstyledButton>
          </Menu.Target>
          <Menu.Dropdown>
            {presets.map((preset, i) => (
              <Menu.Item
                key={preset.id ?? i}
                onClick={() => onChange(toScaleLines(preset.scale_lines))}
              >
                {preset.name || `Scale ${preset.id}`}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      ) : (
        <Stack gap={0} py="xs">
          <Text ta="center" fw={700} size="sm">
            {title}
          </Text>
          {subtitle && (
            <Text ta="center" size="xs" c="dimmed">
              {subtitle}
            </Text>
          )}
        </Stack>
      )}
      <Divider />
      <Group gap={0} style={{ background: ORANGE }}>
        <Text
          style={{ flex: 1 }}
          ta="center"
          fw={700}
          size="sm"
          py="xs"
          c="white"
        >
          Sales
        </Text>
        <Divider orientation="vertical" color="rgba(255,255,255,0.4)" />
        <Text
          style={{ flex: 1 }}
          ta="center"
          fw={700}
          size="sm"
          py="xs"
          c="white"
        >
          {col2Label}
        </Text>
        <Box w={40} />
      </Group>
      <Divider />
      {/* Start row — the base tier, revenue locked at 0. */}
      <Group gap={0} style={{ background: "var(--mantine-color-gray-1)" }}>
        <Text style={{ flex: 1 }} ta="center" fw={700} size="sm" py={6}>
          Start
        </Text>
        <Divider orientation="vertical" />
        <Box style={{ flex: 1 }} py={readOnly ? 6 : 4} px="xs">
          {readOnly ? (
            <Text ta="center" size="sm" fw={600}>
              {rows[0]?.percentage ?? 0}
            </Text>
          ) : (
            <NumberInput
              value={rows[0]?.percentage ?? 0}
              onChange={v => updateLine(0, "percentage", Number(v) || 0)}
              variant="filled"
              size="xs"
              styles={{ input: { textAlign: "center" } }}
              hideControls
            />
          )}
        </Box>
        <Box
          w={40}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {lockIcon}
        </Box>
      </Group>
      {rows.slice(1).map((line, i) => (
        <React.Fragment key={i}>
          <Divider />
          <Group gap={0}>
            <Box style={{ flex: 1 }} py={readOnly ? 6 : 4} px="xs">
              {readOnly ? (
                <Text ta="center" size="sm" fw={700}>
                  {currency(line.revenue_amount)}
                </Text>
              ) : (
                <NumberInput
                  value={line.revenue_amount}
                  onChange={v =>
                    updateLine(i + 1, "revenue_amount", Number(v) || 0)
                  }
                  variant="filled"
                  size="xs"
                  styles={{ input: { textAlign: "center" } }}
                  hideControls
                  thousandSeparator=","
                />
              )}
            </Box>
            <Divider orientation="vertical" />
            <Box style={{ flex: 1 }} py={readOnly ? 6 : 4} px="xs">
              {readOnly ? (
                <Text ta="center" size="sm" fw={600}>
                  {line.percentage}
                </Text>
              ) : (
                <NumberInput
                  value={line.percentage}
                  onChange={v =>
                    updateLine(i + 1, "percentage", Number(v) || 0)
                  }
                  variant="filled"
                  size="xs"
                  styles={{ input: { textAlign: "center" } }}
                  hideControls
                />
              )}
            </Box>
            <Box
              w={40}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {readOnly ? (
                lockIcon
              ) : (
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size="sm"
                  onClick={() => removeLine(i + 1)}
                  aria-label="Remove row"
                >
                  <IconTrash size={14} />
                </ActionIcon>
              )}
            </Box>
          </Group>
        </React.Fragment>
      ))}
      {!readOnly && (
        <>
          <Divider />
          <UnstyledButton
            onClick={addLine}
            style={{ display: "block", width: "100%" }}
          >
            <Text ta="center" py="xs" c="orange" fw={600} size="sm">
              + Add row
            </Text>
          </UnstyledButton>
        </>
      )}
    </Card>
  );
}

// The container for the Upfront Pay / Bonuses / Expenses sections, matching the
// scale tables' chrome: a centered title bar, the added items, and a full-width
// "+ Add" footer that expands the add form inline — items are added in place,
// the same way scale rows are. `onAdd` absent hides the footer (a locked
// template, or the one allowed upfront pay is already set); a locked section
// with nothing in it is hidden entirely, as on iOS.
function ItemTable({ title, addLabel, onAdd, form, empty, children }) {
  if (empty && !onAdd && !form) return null;
  return (
    <Card withBorder radius="md" p={0} style={{ overflow: "hidden" }}>
      <Text ta="center" fw={700} size="sm" py="xs">
        {title}
      </Text>
      {children}
      {form ? (
        <>
          <Divider />
          <Box p="sm">{form}</Box>
        </>
      ) : onAdd ? (
        <>
          <Divider />
          <UnstyledButton
            onClick={onAdd}
            style={{ display: "block", width: "100%" }}
          >
            <Text ta="center" py="xs" c="orange" fw={600} size="sm">
              + {addLabel}
            </Text>
          </UnstyledButton>
        </>
      ) : null}
    </Card>
  );
}

// One added pay-detail item inside an ItemTable: a centered title + subtitle
// (with a trash button) above its rows. `fields` renders the amounts as inline
// editable inputs; `rows` renders plain label/value pairs for a locked section.
function ItemRow({ title, subtitle, fields, rows, onRemove }) {
  return (
    <>
      <Divider />
      <Group gap={0} px="xs" py={6} wrap="nowrap">
        <Box w={28} />
        <Stack gap={0} style={{ flex: 1 }}>
          <Text ta="center" fw={700} size="sm">
            {title}
          </Text>
          {subtitle && (
            <Text ta="center" size="xs" c="dimmed">
              {subtitle}
            </Text>
          )}
        </Stack>
        <Box w={28} style={{ display: "flex", justifyContent: "center" }}>
          {onRemove && (
            <ActionIcon
              variant="subtle"
              color="red"
              size="sm"
              onClick={onRemove}
              aria-label="Remove"
            >
              <IconTrash size={14} />
            </ActionIcon>
          )}
        </Box>
      </Group>
      {(rows || []).map((row, i) => (
        <React.Fragment key={`row-${i}`}>
          <Divider />
          <Group gap="sm" px="md" py={8} wrap="nowrap" justify="space-between">
            <Text size="sm" c="dimmed">
              {row[0]}
            </Text>
            <Text size="sm" fw={600} ta="right">
              {row[1]}
            </Text>
          </Group>
        </React.Fragment>
      ))}
      {(fields || []).map((field, i) => (
        <React.Fragment key={`field-${i}`}>
          <Divider />
          <Group gap="sm" px="md" py={6} wrap="nowrap" justify="space-between">
            <Text size="sm" c="dimmed">
              {field.label}
            </Text>
            {field.onChange ? (
              <NumberInput
                value={field.value ?? ""}
                onChange={v => field.onChange(v === "" ? null : Number(v))}
                variant="filled"
                size="xs"
                w={130}
                hideControls
                styles={{ input: { textAlign: "right" } }}
                {...(field.unit === "money"
                  ? { prefix: "$", thousandSeparator: "," }
                  : field.unit === "percent"
                    ? { suffix: "%" }
                    : {})}
              />
            ) : (
              <Text size="sm" fw={600} ta="right">
                {field.text}
              </Text>
            )}
          </Group>
        </React.Fragment>
      ))}
    </>
  );
}

// A single direct seller's contact card (name / email / phone).
function SellerCard({ label, seller, onChange }) {
  return (
    <Stack gap="xs">
      <Text fw={700} size="sm">
        {label}
      </Text>
      <Card withBorder radius="md" p={0}>
        <Group px="md" py="xs" wrap="nowrap" gap="sm">
          <IconUser size={18} color={ORANGE} />
          <TextInput
            placeholder="Full name"
            value={seller.name}
            onChange={e => onChange({ ...seller, name: e.currentTarget.value })}
            variant="unstyled"
            size="sm"
            style={{ flex: 1 }}
            styles={{ input: { fontWeight: 600 } }}
          />
        </Group>
        <Divider />
        <Group px="md" py="xs" wrap="nowrap" gap="sm">
          <IconMail size={18} color={ORANGE} />
          <TextInput
            placeholder="name@email.com"
            value={seller.email}
            onChange={e =>
              onChange({ ...seller, email: e.currentTarget.value })
            }
            variant="unstyled"
            size="sm"
            style={{ flex: 1 }}
            styles={{ input: { fontWeight: 600 } }}
          />
        </Group>
        <Divider />
        <Group px="md" py="xs" wrap="nowrap" gap="sm">
          <IconPhone size={18} color={ORANGE} />
          <TextInput
            placeholder="(000) 000-0000"
            value={seller.phone}
            onChange={e =>
              onChange({
                ...seller,
                phone: formatPhoneNumber(e.currentTarget.value),
              })
            }
            variant="unstyled"
            size="sm"
            style={{ flex: 1 }}
            styles={{ input: { fontWeight: 600 } }}
          />
        </Group>
      </Card>
    </Stack>
  );
}

// Send an offer to an existing rep: pick the template, tune the pay, review the
// agreement, then create the deal and email the rep the signing link. Mirrors
// the iOS Send Offer flow (SendOfferView) against /v1/ps/user-deal.
function AddRepDealPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, getVerifiedJWT } = useAuth();
  const { showSuccess } = useToast();

  const [step, setStep] = useState(0);
  const [rep, setRep] = useState(null);
  const [overview, setOverview] = useState(null);
  const [selectedYearId, setSelectedYearId] = useState(null);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [overviewError, setOverviewError] = useState(null);
  // null, "save", or "send" — which of the two review actions is in flight.
  const [submitting, setSubmitting] = useState(null);

  // Step 1 — contact + guarantor + rep details.
  const [sellers, setSellers] = useState([{ name: "", email: "", phone: "" }]);
  const [guarantorId, setGuarantorId] = useState(null);
  const [recruitedById, setRecruitedById] = useState(null);
  const [repTypeId, setRepTypeId] = useState(null);
  const [recruitingSourceId, setRecruitingSourceId] = useState(null);
  const [isSplitMD, setIsSplitMD] = useState(false);

  // Step 2 — the chosen template.
  const [templateId, setTemplateId] = useState(null);

  // Step 3 — scale edits, keyed by `comm-<dealUserId>` / `md` / `override`.
  // A key is absent until the table is touched, so an untouched scale always
  // falls back to the template's own lines.
  const [editedScales, setEditedScales] = useState({});

  // Step 3 — the rest of the pay: upfront pay and bonuses per deal user, plus
  // the deal-level expenses. All three are seeded from the chosen template and
  // then added to / edited / removed, mirroring the iOS Pay Details step.
  const [payByUser, setPayByUser] = useState({});
  const [expenses, setExpenses] = useState([]);
  // Which section's add form is open: `upfront-<userKey>`, `bonus-<userKey>`,
  // or `expense`. Only one form is open at a time, as on iOS.
  const [addTarget, setAddTarget] = useState(null);
  // Stable local ids for added bonuses / expenses (the payloads carry none).
  const itemIdRef = useRef(0);
  const nextItemId = () => `item-${++itemIdRef.current}`;

  // Seed the first direct seller from the rep this offer is for.
  useEffect(() => {
    let cancelled = false;
    const fetchRep = async () => {
      try {
        const jwt = await getVerifiedJWT();
        if (!jwt) return;
        const response = await fetch(getApiEndpoint(`/v1/rep/details/${id}`), {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        });
        if (!response.ok) return;
        const result = await response.json();
        const data = result?.data ?? result;
        if (cancelled || !data) return;
        setRep(data);
        setSellers([
          {
            name: `${data.first_name || ""} ${data.last_name || ""}`.trim(),
            email: data.email || "",
            phone: formatPhoneNumber(data.phone_number || ""),
          },
        ]);
      } catch (err) {
        // A failed prefill just leaves the contact fields empty to fill in.
      }
    };
    fetchRep();
    return () => {
      cancelled = true;
    };
  }, [id, getVerifiedJWT]);

  // The overview is scoped to one selling year server-side, so changing the year
  // refetches it (and re-scopes the available templates).
  const fetchOverview = useCallback(
    async yearId => {
      try {
        setLoadingOverview(true);
        setOverviewError(null);
        const jwt = await getVerifiedJWT();
        if (!jwt) throw new Error("Authentication failed");

        const response = await fetch(
          getApiEndpoint(
            yearId != null
              ? `/v1/ps/overview?selling_year_id=${yearId}`
              : "/v1/ps/overview"
          ),
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
          }
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const result = await response.json();
        const data = result?.data ?? result ?? {};
        setOverview(data);
        setSelectedYearId(
          data.selected_selling_year_id != null
            ? String(data.selected_selling_year_id)
            : yearId != null
              ? String(yearId)
              : null
        );
        return data;
      } catch (err) {
        setOverviewError("Failed to load offer templates");
        return null;
      } finally {
        setLoadingOverview(false);
      }
    },
    [getVerifiedJWT]
  );

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const sellingYears = useMemo(
    () =>
      (Array.isArray(overview?.selling_years) ? overview.selling_years : [])
        .slice()
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [overview]
  );

  // Managers can offer their own templates on top of the company's; company
  // admins see only the company templates.
  const templates = useMemo(() => {
    const company = Array.isArray(overview?.company_templates)
      ? overview.company_templates
      : [];
    const mine =
      user?.role === "MANAGER" && Array.isArray(overview?.my_templates)
        ? overview.my_templates
        : [];
    return [...company, ...mine];
  }, [overview, user]);

  const selectedTemplate = useMemo(
    () => templates.find(t => String(t.id) === String(templateId)) || null,
    [templates, templateId]
  );

  const templateDealUsers = useMemo(
    () =>
      Array.isArray(selectedTemplate?.deal_users)
        ? selectedTemplate.deal_users
        : [],
    [selectedTemplate]
  );

  // One direct seller per deal user — a split template collects more than one.
  const dealUserCount = Math.max(1, templateDealUsers.length);

  // Resize the seller list whenever the template changes, keeping what's already
  // entered, and drop scale edits so they never carry across templates.
  useEffect(() => {
    setEditedScales({});
    setSellers(prev => {
      if (prev.length === dealUserCount) return prev;
      if (prev.length < dealUserCount) {
        return [
          ...prev,
          ...Array.from({ length: dealUserCount - prev.length }, () => ({
            name: "",
            email: "",
            phone: "",
          })),
        ];
      }
      return prev.slice(0, dealUserCount);
    });
  }, [templateId, dealUserCount]);

  // Seed the editable pay details from the chosen template. Everything the
  // template already carries is collected up front so it can be tuned or
  // removed, and anything added on top joins the same lists.
  useEffect(() => {
    setAddTarget(null);
    if (!selectedTemplate) {
      setPayByUser({});
      setExpenses([]);
      return;
    }
    const users = templateDealUsers.length > 0 ? templateDealUsers : [null];
    const seeded = {};
    users.forEach(dealUser => {
      seeded[dealUserKey(dealUser)] = {
        upfront: dealUser?.upfront_pay
          ? templateUpfrontPay(dealUser.upfront_pay)
          : null,
        bonuses: (dealUser?.default_bonuses || []).map(b => ({
          _id: nextItemId(),
          ...templateBonus(b),
        })),
      };
    });
    setPayByUser(seeded);
    setExpenses(
      (selectedTemplate.default_expenses || []).map(e => ({
        _id: nextItemId(),
        ...templateExpense(e),
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate, templateDealUsers]);

  const guarantorOptions = useMemo(() => {
    const list = Array.isArray(overview?.guarantor_options)
      ? overview.guarantor_options
      : [];
    return list.map(g => {
      const names = (Array.isArray(g.deal_users) ? g.deal_users : [])
        .map(
          u =>
            u.name ||
            `${u.first_name || ""} ${u.last_name || ""}`.trim() ||
            u.email ||
            ""
        )
        .filter(Boolean);
      return {
        value: String(g.id),
        label: g.is_company_deal
          ? g.company_name || `Guarantor ${g.id}`
          : names.join(" & ") || g.company_name || `Guarantor ${g.id}`,
      };
    });
  }, [overview]);

  // Default the guarantor to the deal the signed-in user is on, then the company
  // deal, then the first option — the same precedence as the iOS flow.
  useEffect(() => {
    const list = Array.isArray(overview?.guarantor_options)
      ? overview.guarantor_options
      : [];
    if (list.length === 0) return;
    setGuarantorId(prev => {
      if (prev != null && list.some(g => String(g.id) === String(prev))) {
        return prev;
      }
      const preferred =
        list.find(g => g.is_current_user_deal) ||
        list.find(g => g.is_company_deal) ||
        list[0];
      return String(preferred.id);
    });
  }, [overview]);

  const optionList = (items, labelKeys) =>
    (Array.isArray(items) ? items : []).map(item => ({
      value: String(item.id),
      label:
        labelKeys.map(k => item[k]).find(v => v) ||
        `${[item.first_name, item.last_name].filter(Boolean).join(" ")}`.trim() ||
        item.email ||
        `#${item.id}`,
    }));

  const presets = useMemo(
    () => groupScalesByType(overview?.company_default_scales),
    [overview]
  );

  const isMd = selectedTemplate?.deal_type === "MD";

  // The template's own lines for a scale key, used until the table is edited.
  const templateScaleLines = useCallback(
    key => {
      if (key === "md")
        return toScaleLines(selectedTemplate?.md_scale?.scale_lines);
      if (key === "override") {
        return toScaleLines(selectedTemplate?.override_scale?.scale_lines);
      }
      const dealUserId = key.startsWith("comm-") ? key.slice(5) : null;
      const dealUser = templateDealUsers.find(
        du => String(du.id) === String(dealUserId)
      );
      return toScaleLines(dealUser?.commission_scale?.scale_lines);
    },
    [selectedTemplate, templateDealUsers]
  );

  const scaleLines = useCallback(
    key => editedScales[key] ?? templateScaleLines(key),
    [editedScales, templateScaleLines]
  );

  const setScaleLines = (key, lines) =>
    setEditedScales(prev => ({ ...prev, [key]: lines }));

  // A non-customizable template's pay is shown but can't be touched — the same
  // lock the embedded iOS pay editor applies.
  const payLocked = !selectedTemplate?.is_customizable;

  const userPay = key => payByUser[key] ?? { upfront: null, bonuses: [] };

  const setUserUpfront = (key, upfront) =>
    setPayByUser(prev => ({
      ...prev,
      [key]: { bonuses: [], ...prev[key], upfront },
    }));

  const updateUserBonuses = (key, update) =>
    setPayByUser(prev => {
      const entry = prev[key] ?? { upfront: null, bonuses: [] };
      return { ...prev, [key]: { ...entry, bonuses: update(entry.bonuses) } };
    });

  const sellerLabel = index => {
    if (dealUserCount <= 1) return "Direct Seller";
    const dealUser = templateDealUsers[index];
    return dealUser
      ? `Direct Seller ${index + 1} · ${percent(dealUser.deal_percentage)}`
      : `Direct Seller ${index + 1}`;
  };

  // The default-deal-shaped payload: what /v1/ps/review renders, and the base of
  // the user-deal create body.
  const dealPayload = useMemo(() => {
    if (!selectedTemplate) return null;
    const users = (
      templateDealUsers.length > 0 ? templateDealUsers : [null]
    ).map(dealUser => {
      const pay = payByUser[dealUserKey(dealUser)] ?? {};
      return {
        percentage: dealUser?.deal_percentage ?? 100,
        commission_scale_lines: normalizeScaleLines(
          editedScales[`comm-${dealUser?.id}`] ??
            toScaleLines(dealUser?.commission_scale?.scale_lines)
        ),
        upfront_pay: pay.upfront ?? undefined,
        // The local `_id` is dropped — the create DTO's whitelist rejects it.
        bonuses: (pay.bonuses || []).map(({ _id, ...bonus }) => bonus),
      };
    });

    const payload = {
      selling_year_id:
        selectedYearId != null
          ? Number(selectedYearId)
          : selectedTemplate.selling_year_id,
      name: selectedTemplate.name,
      deal_type: selectedTemplate.deal_type,
      is_customizable: selectedTemplate.is_customizable,
      template_id: selectedTemplate.template_id ?? null,
      users,
      expenses: expenses.map(({ _id, ...expense }) => expense),
    };

    if (isMd) {
      payload.md_scale_lines = normalizeScaleLines(scaleLines("md"));
    } else {
      payload.allow_override = !!selectedTemplate.has_override;
      if (selectedTemplate.has_override) {
        payload.override_scale_lines = normalizeScaleLines(
          scaleLines("override")
        );
      }
    }
    return payload;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedTemplate,
    templateDealUsers,
    editedScales,
    payByUser,
    expenses,
    selectedYearId,
    isMd,
    scaleLines,
  ]);

  const canAdvanceDetails = guarantorId != null && sellerIsValid(sellers[0]);
  const canAdvanceTemplate =
    selectedTemplate != null &&
    sellers.length === dealUserCount &&
    sellers.every(sellerIsValid);

  // Create the deal. `sendOffer` true emails the seller their signing link;
  // false saves the deal and leaves its document an unsent draft.
  const handleSubmit = async sendOffer => {
    if (!dealPayload || !selectedTemplate) return;
    const failureMessage = sendOffer
      ? "Failed to send offer"
      : "Failed to save deal";
    try {
      setSubmitting(sendOffer ? "send" : "save");
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const payload = {
        ...dealPayload,
        guarantor_id: Number(guarantorId),
        default_deal_id: Number(selectedTemplate.id),
        // Only sent for the draft path: the endpoint already sends by default,
        // so omitting it on Send Offer keeps that button working against an API
        // that predates the flag (unknown fields are rejected outright).
        ...(sendOffer ? {} : { send_offer: false }),
        users: dealPayload.users.map((u, index) => {
          const seller = sellers[index] || sellers[0];
          return {
            ...u,
            direct_seller_name: (seller?.name || "").trim(),
            direct_seller_email: (seller?.email || "").trim(),
            direct_seller_phone: (seller?.phone || "").trim() || null,
            recruited_by_user_id:
              recruitedById != null ? Number(recruitedById) : null,
            recruiting_source_id:
              recruitingSourceId != null ? Number(recruitingSourceId) : null,
            rep_type_id: repTypeId != null ? Number(repTypeId) : null,
          };
        }),
      };

      const response = await fetch(getApiEndpoint("/v1/ps/user-deal"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        showSuccess(
          sendOffer ? "Offer sent successfully!" : "Deal saved successfully!"
        );
        navigate(`/reps/${id}?tab=deal`);
      } else {
        const errorData = await response.json().catch(() => ({}));
        handleApiError(
          new Error(errorData.message || `HTTP ${response.status}`),
          errorData.message || failureMessage
        );
      }
    } catch (err) {
      handleGenericError(err, failureMessage);
    } finally {
      setSubmitting(null);
    }
  };

  const repName = rep
    ? `${rep.first_name || ""} ${rep.last_name || ""}`.trim()
    : "";

  const headerActions = (
    <Button
      variant="outline"
      leftSection={<IconArrowLeft size={16} />}
      onClick={() => navigate(`/reps/${id}?tab=deal`)}
    >
      Back to Rep
    </Button>
  );

  const renderDetailsStep = () => (
    <Stack gap="lg">
      <SellerCard
        label={sellerLabel(0)}
        seller={sellers[0] || { name: "", email: "", phone: "" }}
        onChange={next =>
          setSellers(prev => prev.map((s, i) => (i === 0 ? next : s)))
        }
      />

      <Group justify="space-between">
        <Text size="sm" fw={700}>
          Split MD Deal
        </Text>
        <Switch
          checked={isSplitMD}
          onChange={e => {
            const next = e.currentTarget.checked;
            setIsSplitMD(next);
            if (
              next &&
              selectedTemplate &&
              (selectedTemplate.deal_type !== "MD" ||
                selectedTemplate.is_split ||
                selectedTemplate.is_customizable)
            ) {
              setTemplateId(null);
            }
          }}
          color="orange"
        />
      </Group>

      <Stack gap="xs">
        <Text fw={700} size="sm">
          Guarantor
        </Text>
        <Select
          placeholder="Select guarantor"
          data={guarantorOptions}
          value={guarantorId}
          onChange={setGuarantorId}
          searchable
          allowDeselect={false}
        />
      </Stack>

      <Stack gap="xs">
        <Text fw={700} size="sm">
          Rep Details
        </Text>
        <Select
          label="Recruited By"
          placeholder="Select rep"
          data={optionList(overview?.recruited_by_options, ["name"])}
          value={recruitedById}
          onChange={setRecruitedById}
          searchable
          clearable
        />
        <Select
          label="Rep Type"
          placeholder="Select type"
          data={optionList(overview?.rep_types, ["name", "rep_type_name"])}
          value={repTypeId}
          onChange={setRepTypeId}
          searchable
          clearable
        />
        <Select
          label="Recruiting Source"
          placeholder="Select source"
          data={optionList(overview?.recruiting_sources, [
            "name",
            "source_name",
          ])}
          value={recruitingSourceId}
          onChange={setRecruitingSourceId}
          searchable
          clearable
        />
      </Stack>

      <Button
        fullWidth
        color="orange"
        disabled={!canAdvanceDetails}
        onClick={() => setStep(1)}
      >
        Continue
      </Button>
    </Stack>
  );

  const renderTemplateStep = () => (
    <Stack gap="lg">
      <Stack gap="xs">
        <Group justify="space-between" align="center">
          <Text fw={700} size="sm">
            Offer Template
          </Text>
          <Select
            placeholder="Season"
            data={sellingYears.map(y => ({
              value: String(y.id),
              label: y.name,
            }))}
            value={selectedYearId}
            onChange={value => {
              setSelectedYearId(value);
              setTemplateId(null);
              fetchOverview(value);
            }}
            allowDeselect={false}
            w={140}
            size="xs"
            rightSection={<IconChevronDown size={12} />}
          />
        </Group>

        {loadingOverview ? (
          <Center py="md">
            <Loader size="sm" />
          </Center>
        ) : templates.length === 0 ? (
          <Text size="sm" c="dimmed">
            No templates available for this season.
          </Text>
        ) : (
          <Card withBorder radius="md" p={0}>
            {templates.map((template, i) => {
              const isSelected = String(template.id) === String(templateId);
              const isDisabled =
                isSplitMD &&
                (template.deal_type !== "MD" ||
                  template.is_split ||
                  template.is_customizable);
              const parts = [
                template.deal_type === "MD" ? "Marketing Deal" : "Rep",
                template.is_split ? "Split" : null,
                template.has_override ? "Override" : null,
              ].filter(Boolean);
              return (
                <React.Fragment key={template.id}>
                  {i > 0 && <Divider />}
                  <UnstyledButton
                    onClick={() => !isDisabled && setTemplateId(template.id)}
                    style={{
                      display: "block",
                      width: "100%",
                      opacity: isDisabled ? 0.4 : 1,
                      cursor: isDisabled ? "not-allowed" : "pointer",
                    }}
                  >
                    <Group
                      px="md"
                      py="sm"
                      wrap="nowrap"
                      justify="space-between"
                    >
                      <Stack gap={2}>
                        <Group gap="xs" wrap="nowrap">
                          <Text size="sm" fw={600}>
                            {template.name}
                          </Text>
                          {template.is_customizable && (
                            <Badge
                              size="xs"
                              variant="filled"
                              color="orange"
                              tt="uppercase"
                            >
                              Customizable
                            </Badge>
                          )}
                        </Group>
                        <Text size="xs" c="dimmed">
                          {parts.join(" · ")}
                        </Text>
                      </Stack>
                      {isSelected ? (
                        <IconCircleCheck size={22} color={ORANGE} />
                      ) : (
                        <IconCircle
                          size={22}
                          color="var(--mantine-color-gray-4)"
                        />
                      )}
                    </Group>
                  </UnstyledButton>
                </React.Fragment>
              );
            })}
          </Card>
        )}
      </Stack>

      {/* A split template needs one direct seller per deal user; the first was
          collected on the Offer Details step. */}
      {sellers.slice(1).map((seller, i) => (
        <SellerCard
          key={i + 1}
          label={sellerLabel(i + 1)}
          seller={seller}
          onChange={next =>
            setSellers(prev => prev.map((s, idx) => (idx === i + 1 ? next : s)))
          }
        />
      ))}

      <Button
        fullWidth
        color="orange"
        disabled={!canAdvanceTemplate}
        onClick={() => setStep(2)}
      >
        Continue
      </Button>
    </Stack>
  );

  const renderPayStep = () => {
    const multiple = templateDealUsers.length > 1;
    // A template with no deal users still collects the one seller's pay.
    const payUsers = templateDealUsers.length > 0 ? templateDealUsers : [null];
    const withWho = (label, who) => (multiple ? `${who} · ${label}` : label);

    return (
      <Stack gap="lg">
        {isMd && (
          <ScaleTable
            title="MD Scale"
            col2Label="MD %"
            lines={scaleLines("md")}
            onChange={lines => setScaleLines("md", lines)}
            presets={presets.MD}
            readOnly={payLocked}
          />
        )}

        {payUsers.map((dealUser, index) => {
          const who =
            (sellers[index]?.name || "").trim() || `Direct Seller ${index + 1}`;
          const key = dealUserKey(dealUser);
          const pay = userPay(key);
          const upfront = pay.upfront;
          return (
            <React.Fragment key={key}>
              {dealUser && (
                <ScaleTable
                  title={withWho("Commission Scale", who)}
                  subtitle={
                    multiple ? percent(dealUser.deal_percentage) : undefined
                  }
                  col2Label="Commission"
                  lines={scaleLines(`comm-${dealUser.id}`)}
                  onChange={lines =>
                    setScaleLines(`comm-${dealUser.id}`, lines)
                  }
                  presets={presets.COMMISSION}
                  readOnly={payLocked}
                />
              )}

              {/* Upfront pay is one per seller, so the add footer goes away
                  once it's set. */}
              <ItemTable
                title={withWho("Upfront Pay", who)}
                addLabel="Add Upfront Pay"
                empty={!upfront}
                onAdd={
                  payLocked || upfront
                    ? null
                    : () => setAddTarget(`upfront-${key}`)
                }
                form={
                  addTarget === `upfront-${key}` ? (
                    <AddUpfrontPayForm
                      companyUpfrontTypes={overview?.ps_company_upfront_types}
                      onCreated={created => {
                        setUserUpfront(key, created);
                        setAddTarget(null);
                      }}
                      onCancel={() => setAddTarget(null)}
                    />
                  ) : null
                }
              >
                {upfront && (
                  <ItemRow
                    title={
                      UPFRONT_PAY_TYPE_LABELS[upfront.upfront_pay_type] ||
                      "Upfront Pay"
                    }
                    fields={
                      payLocked
                        ? null
                        : upfrontFields(upfront, next =>
                            setUserUpfront(key, next)
                          )
                    }
                    rows={payLocked ? upfrontConfig(upfront).rows : null}
                    onRemove={
                      payLocked ? null : () => setUserUpfront(key, null)
                    }
                  />
                )}
              </ItemTable>

              <ItemTable
                title={withWho("Bonuses", who)}
                addLabel="Add Bonus"
                empty={pay.bonuses.length === 0}
                onAdd={payLocked ? null : () => setAddTarget(`bonus-${key}`)}
                form={
                  addTarget === `bonus-${key}` ? (
                    <AddBonusForm
                      inline
                      sellingYearId={
                        selectedYearId != null ? Number(selectedYearId) : null
                      }
                      companyBonusTypes={overview?.ps_company_bonus_types}
                      onCreated={created => {
                        updateUserBonuses(key, prev => [
                          ...prev,
                          { _id: nextItemId(), ...created },
                        ]);
                        setAddTarget(null);
                      }}
                      onCancel={() => setAddTarget(null)}
                    />
                  ) : null
                }
              >
                {pay.bonuses.map(bonus => (
                  <ItemRow
                    key={bonus._id}
                    title={bonus.name || "Bonus"}
                    subtitle={
                      BONUS_CATEGORY_LABELS[bonus.bonus_type_category] ||
                      titleize(bonus.bonus_type_category)
                    }
                    fields={
                      payLocked
                        ? null
                        : bonusFields(bonus, next =>
                            updateUserBonuses(key, prev =>
                              prev.map(b =>
                                b._id === bonus._id
                                  ? { ...next, _id: bonus._id }
                                  : b
                              )
                            )
                          )
                    }
                    rows={payLocked ? bonusRows(bonus) : null}
                    onRemove={
                      payLocked
                        ? null
                        : () =>
                            updateUserBonuses(key, prev =>
                              prev.filter(b => b._id !== bonus._id)
                            )
                    }
                  />
                ))}
              </ItemTable>
            </React.Fragment>
          );
        })}

        {!isMd && selectedTemplate?.has_override && (
          <ScaleTable
            title="Override Scale"
            col2Label="Override %"
            lines={scaleLines("override")}
            onChange={lines => setScaleLines("override", lines)}
            presets={presets.OVERRIDE}
            readOnly={payLocked}
          />
        )}

        {/* Expenses come off the top of the whole deal, not one seller's pay. */}
        <ItemTable
          title="Expenses"
          addLabel="Add Expense"
          empty={expenses.length === 0}
          onAdd={payLocked ? null : () => setAddTarget("expense")}
          form={
            addTarget === "expense" ? (
              <AddExpenseForm
                inline
                sellingYearId={
                  selectedYearId != null ? Number(selectedYearId) : null
                }
                companyExpenseTypes={overview?.ps_company_expense_types}
                onCreated={created => {
                  setExpenses(prev => [
                    ...prev,
                    { _id: nextItemId(), ...created },
                  ]);
                  setAddTarget(null);
                }}
                onCancel={() => setAddTarget(null)}
              />
            ) : null
          }
        >
          {expenses.map(expense => (
            <ItemRow
              key={expense._id}
              title={expense.name || "Expense"}
              subtitle={
                EXPENSE_CATEGORY_LABELS[expense.expense_type_category] ||
                titleize(expense.expense_type_category)
              }
              fields={
                payLocked
                  ? null
                  : expenseFields(expense, next =>
                      setExpenses(prev =>
                        prev.map(e =>
                          e._id === expense._id
                            ? { ...next, _id: expense._id }
                            : e
                        )
                      )
                    )
              }
              rows={payLocked ? expenseRows(expense) : null}
              onRemove={
                payLocked
                  ? null
                  : () =>
                      setExpenses(prev =>
                        prev.filter(e => e._id !== expense._id)
                      )
              }
            />
          ))}
        </ItemTable>

        <Button fullWidth color="orange" onClick={() => setStep(3)}>
          Review offer
        </Button>
      </Stack>
    );
  };

  const renderReviewStep = () => (
    <Stack gap="lg">
      {dealPayload && <DealReview {...dealPayload} />}
      <Group grow>
        <Button
          variant="default"
          leftSection={<IconDeviceFloppy size={16} />}
          loading={submitting === "save"}
          disabled={!dealPayload || submitting === "send"}
          onClick={() => handleSubmit(false)}
        >
          Save Deal
        </Button>
        <Button
          color="orange"
          leftSection={<IconSend size={16} />}
          loading={submitting === "send"}
          disabled={!dealPayload || submitting === "save"}
          onClick={() => handleSubmit(true)}
        >
          Send Offer
        </Button>
      </Group>
      <Text size="xs" c="dimmed" ta="center">
        Save Deal makes the deal active now and leaves the agreement an unsent
        draft. Send Offer emails {sellers[0]?.name || "the seller"} the signing
        link instead — that deal goes active once it's fully signed. Either way
        any existing deal for the season is superseded.
      </Text>
    </Stack>
  );

  return (
    <PageContent
      breadcrumbs={[
        { label: "Reps", path: "/downline" },
        { label: repName || "Rep", path: `/reps/${id}` },
        { label: "Add Deal" },
      ]}
      title="Add Deal"
      subtitle={repName ? `Send an offer to ${repName}` : "Send an offer"}
      headerActions={headerActions}
    >
      <Stack gap="lg" style={{ maxWidth: 720 }}>
        <Group gap="xs">
          {step > 0 && (
            <ActionIcon variant="subtle" onClick={() => setStep(step - 1)}>
              <IconChevronLeft size={18} />
            </ActionIcon>
          )}
          <Text fw={600}>{STEPS[step]}</Text>
        </Group>

        <Stack gap={4}>
          <Progress
            value={((step + 1) / STEPS.length) * 100}
            size="xs"
            color="orange"
          />
          <Text size="xs" c="dimmed" ta="center" tt="uppercase" fw={600}>
            Step {step + 1} / {STEPS.length}
          </Text>
        </Stack>

        {overviewError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {overviewError}
          </Alert>
        )}

        {loadingOverview && step === 0 ? (
          <Center py="xl">
            <Loader />
          </Center>
        ) : step === 0 ? (
          renderDetailsStep()
        ) : step === 1 ? (
          renderTemplateStep()
        ) : step === 2 ? (
          renderPayStep()
        ) : (
          renderReviewStep()
        )}
      </Stack>
    </PageContent>
  );
}

export default AddRepDealPage;
